import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table as MuiTable,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { formattedAmount } from "utils/formattedAmount";

type TaxType = "IGST" | "CGST" | "SGST" | "CESS" | "TOTAL";

interface GstTaxBreakdownDialogProps {
  open: boolean;
  onClose: () => void;
  row: any;
  type: TaxType;
}

const GstTaxBreakdownDialog: React.FC<GstTaxBreakdownDialogProps> = ({
  open,
  onClose,
  row,
  type,
}) => {
  const taxKeys = ["Tx", "Pen", "Fee", "Intr", "Oth"];
  const taxLabels: Record<string, string> = {
    Tx: "Tax",
    Pen: "Penalty",
    Fee: "Fee",
    Intr: "Interest",
    Oth: "Others",
  };

  const taxTypes = ["igst", "cgst", "sgst", "cess"];

  const renderTotalBreakdown = () => (
    <MuiTable size="small">
      <TableHead>
        <TableRow>
          <TableCell></TableCell>
          {taxKeys.map((key) => (
            <TableCell key={key}>{taxLabels[key]}</TableCell>
          ))}
          <TableCell>Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {taxTypes.map((taxType) => {
          const values = taxKeys.map((suffix) => row?.[`${taxType}${suffix}`] ?? 0);
          const total = values.reduce((acc, val) => acc + Number(val), 0);
          return (
            <TableRow key={taxType}>
              <TableCell>{taxType.toUpperCase()}</TableCell>
              {values.map((value, idx) => (
                <TableCell key={idx}>
                  <Box display="flex" alignItems="center">
                    <CurrencyRupeeIcon fontSize="small" sx={{ mr: 0.3, width: "15px" }} />
                    {value}
                  </Box>
                </TableCell>
              ))}
              <TableCell>
                <Box display="flex" alignItems="center">
                  <CurrencyRupeeIcon fontSize="small" sx={{ mr: 0.3, width: "15px" }} />
                  {total}
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </MuiTable>
  );

  const taxColumnsMap: Record<TaxType, { label: string; key: string }[]> = {
    IGST: [
      { label: "Tax", key: "igstTx" },
      { label: "Fee", key: "igstFee" },
      { label: "Penalty", key: "igstPen" },
      { label: "Interest", key: "igstIntr" },
      { label: "Others", key: "igstOth" },
    ],
    CGST: [
      { label: "Tax", key: "cgstTx" },
      { label: "Fee", key: "cgstFee" },
      { label: "Penalty", key: "cgstPen" },
      { label: "Interest", key: "cgstIntr" },
      { label: "Others", key: "cgstOth" },
    ],
    SGST: [
      { label: "Tax", key: "sgstTx" },
      { label: "Fee", key: "sgstFee" },
      { label: "Penalty", key: "sgstPen" },
      { label: "Interest", key: "sgstIntr" },
      { label: "Others", key: "sgstOth" },
    ],
    CESS: [
      { label: "Tax", key: "cessTx" },
      { label: "Fee", key: "cessFee" },
      { label: "Penalty", key: "cessPen" },
      { label: "Interest", key: "cessIntr" },
      { label: "Others", key: "cessOth" },
    ],
    TOTAL: [], // handled separately
  };

  const headers = taxColumnsMap[type]?.map((item) => item.label) ?? [];
  const keys = taxColumnsMap[type]?.map((item) => item.key) ?? [];

  const renderValues = (keys: string[]) => (
    <TableRow>
      {keys.map((key) => (
        <TableCell key={key}>
          <Box display="flex" alignItems="center">
            {formattedAmount(row?.[key]) ?? 0}
          </Box>
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {type} Breakdown
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!row ? (
          <Typography>No data available</Typography>
        ) : type === "TOTAL" ? (
          renderTotalBreakdown()
        ) : (
          <MuiTable size="small">
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{renderValues(keys)}</TableBody>
          </MuiTable>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GstTaxBreakdownDialog;
